import type { AssessmentType } from '@mend/types/assessment';
import type { AutocompleteProps } from '@mui/material';
import type { BaseAutocompleteInputProps } from './Autocomplete.types.ts';

import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { matchSorter } from '#lib/match-sorter.ts';
import { assessments as assessmentsQueryOptions } from '#lib/react-query/queries.ts';
import { getTextFieldProps } from './Autocomplete.utils.ts';

const defaultPreFilterOptions = (options: AssessmentType[]) => options;

interface FormsAutocompleteProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>
  extends
    Omit<
      AutocompleteProps<AssessmentType, Multiple, DisableClearable, false>,
      | 'loading'
      | 'options'
      | 'getOptionKey'
      | 'getOptionLabel'
      | 'filterOptions'
      | 'isOptionEqualToValue'
      | 'onInputChange'
      | 'renderInput'
      | 'renderOption'
      | 'renderTags'
      | 'freeSolo'
    >,
    BaseAutocompleteInputProps<AssessmentType> {
  /**
   * Pre-filter the options before the input based filter is applied.
   */
  preFilterOptions?: (options: AssessmentType[]) => AssessmentType[];
}

export function FormsAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  inputName,
  inputLabel = 'Search',
  inputHelperText,
  inputError,
  inputRequired,
  inputSize = 'small',
  fullWidth = true,
  filterSelectedOptions = true,
  clearOnBlur = false,
  selectOnFocus = true,
  handleHomeEndKeys = true,
  value,
  multiple,
  preFilterOptions = defaultPreFilterOptions,
  ...props
}: FormsAutocompleteProps<Multiple, DisableClearable>): JSX.Element {
  const assessmentsQuery = useQuery(assessmentsQueryOptions);

  return (
    <Autocomplete
      value={value}
      freeSolo={false}
      multiple={multiple}
      fullWidth={fullWidth}
      clearOnBlur={clearOnBlur}
      selectOnFocus={selectOnFocus}
      handleHomeEndKeys={handleHomeEndKeys}
      loading={assessmentsQuery.isLoading}
      options={assessmentsQuery.data ?? []}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => option.title}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={(options, { inputValue }) =>
        matchSorter(preFilterOptions(options), inputValue, { keys: ['title'] })
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...getTextFieldProps({ params, multiple, inputRequired })}
          name={inputName}
          label={inputLabel}
          helperText={inputHelperText}
          error={inputError}
          size={inputSize}
        />
      )}
      {...props}
    />
  );
}
